import { useQuery } from "@tanstack/react-query";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";

interface EncerrarDemaisFieldProps {
  id: string;
  vagaId: string | null;
  /** O vencedor nunca entra no lote, mesmo numa etapa ativa (sql/160). */
  freelancerAprovadoId: string | null;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}

interface KanbanQuery extends PromiseLike<{ data: Record<string, string | null>[] | null; error: Error | null }> {
  select(columns: string): KanbanQuery;
  eq(column: string, value: string): KanbanQuery;
  in(column: string, values: string[]): KanbanQuery;
  or(filters: string): KanbanQuery;
}

// As tabelas vagas_kanban_* não estão nos tipos gerados do Supabase.
const kanbanTable = (table: string) => (supabase as unknown as { from: (name: string) => KanbanQuery }).from(table);

/** Freelancers em etapa ativa e não dropados: o mesmo recorte que encerrar_candidatos_restantes usa. */
async function fetchActiveFreelancerIds(vagaId: string): Promise<(string | null)[]> {
  const { data: etapas, error: etapasError } = await kanbanTable("vagas_kanban_etapas")
    .select("id")
    .eq("vaga_id", vagaId)
    .eq("tipo", "ativa");
  if (etapasError) throw etapasError;
  const etapaIds = (etapas ?? []).flatMap((etapa) => (etapa.id ? [etapa.id] : []));
  if (etapaIds.length === 0) return [];
  const { data: posicoes, error: posicoesError } = await kanbanTable("vagas_kanban_posicoes")
    .select("freelancer_id")
    .eq("vaga_id", vagaId)
    .in("etapa_atual_id", etapaIds)
    .or("is_dropped.is.null,is_dropped.eq.false");
  if (posicoesError) throw posicoesError;
  return (posicoes ?? []).map((posicao) => posicao.freelancer_id);
}

export function EncerrarDemaisField({
  id,
  vagaId,
  freelancerAprovadoId,
  checked,
  onCheckedChange,
  disabled = false,
}: EncerrarDemaisFieldProps) {
  const { data: activeFreelancerIds } = useQuery({
    queryKey: ["vaga-candidatos-ativos", vagaId],
    queryFn: () => fetchActiveFreelancerIds(vagaId as string),
    enabled: !!vagaId,
    staleTime: 0,
  });

  const count = activeFreelancerIds?.filter(
    (freelancerId) => !freelancerAprovadoId || (freelancerId !== null && freelancerId !== freelancerAprovadoId),
  ).length;

  const detail =
    count === undefined
      ? "Os candidatos que não foram aprovados saem do kanban com o motivo Vaga preenchida."
      : count === 0
      ? "Nenhum outro candidato está em andamento no kanban."
      : `${count} ${count === 1 ? "candidato irá" : "candidatos irão"} para Reprovado com o motivo Vaga preenchida.`;

  return (
    <div className="flex items-start gap-2 rounded-md border p-3">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(value) => onCheckedChange(value === true)}
        disabled={disabled}
      />
      <div className="grid gap-1 leading-none">
        <Label htmlFor={id} className="cursor-pointer">
          Encerrar os demais candidatos do processo
        </Label>
        <p className="text-xs text-muted-foreground">{detail}</p>
      </div>
    </div>
  );
}
